import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  findDrivingCourseById,
  type DrivingCourseOption,
} from '../data/courseOptions';
import {
  findTimeOfDayById,
  type TimeOfDayOption,
} from '../data/timeOfDayOptions';
import {
  findDrivingCameraById,
  type DrivingCameraMode,
  type DrivingCameraOption,
} from '../data/drivingCameraOptions';
import {
  findDrivingChallengeById,
  type DrivingChallengeId,
  type DrivingChallengeOption,
} from '../data/challengeOptions';
import {
  type DrivingAssistId,
  type DrivingPreferences,
  type RegenMode,
} from '../model/drivingPreferences';
import {
  readDrivingPreferences,
  writeDrivingPreferences,
} from '../model/drivingStorage';
import {
  DEFAULT_VEHICLE_TUNING,
  isValidTuningValue,
  type VehicleTuningField,
} from '../model/vehicleTuning';

export interface DrivingPreferencesController {
  preferences: DrivingPreferences;
  course: DrivingCourseOption;
  timeOfDay: TimeOfDayOption;
  camera: DrivingCameraOption;
  challenge: DrivingChallengeOption;
  onCourseSelect: (courseId: string) => void;
  onTimeOfDaySelect: (timeOfDayId: string) => void;
  onCameraSelect: (mode: DrivingCameraMode) => void;
  onWeatherModeToggle: () => void;
  onChallengeSelect: (id: DrivingChallengeId) => void;
  onRegenModeSelect: (mode: RegenMode) => void;
  onAssistToggle: (assist: DrivingAssistId) => void;
  onTuningSelect: (field: VehicleTuningField, value: string) => void;
  onTuningReset: () => void;
}

export function useDrivingPreferences(): DrivingPreferencesController {
  const [preferences, setPreferences] = useState(readDrivingPreferences);

  useEffect(() => {
    writeDrivingPreferences(preferences);
  }, [preferences]);

  const onCourseSelect = useCallback((courseId: string) => {
    if (findDrivingCourseById(courseId) === undefined) return;
    setPreferences((current) =>
      current.courseId === courseId ? current : { ...current, courseId }
    );
  }, []);

  const onTimeOfDaySelect = useCallback((timeOfDayId: string) => {
    if (findTimeOfDayById(timeOfDayId) === undefined) return;
    setPreferences((current) =>
      current.timeOfDayId === timeOfDayId ? current : { ...current, timeOfDayId }
    );
  }, []);

  const onCameraSelect = useCallback((cameraMode: DrivingCameraMode) => {
    if (findDrivingCameraById(cameraMode) === undefined) return;
    setPreferences((current) => ({ ...current, cameraMode }));
  }, []);

  const onWeatherModeToggle = useCallback(() => {
    setPreferences((current) => ({
      ...current,
      weatherMode: current.weatherMode === 'fixed' ? 'dynamic' : 'fixed',
    }));
  }, []);

  const onChallengeSelect = useCallback((challengeId: DrivingChallengeId) => {
    if (findDrivingChallengeById(challengeId) === undefined) return;
    setPreferences((current) => ({ ...current, challengeId }));
  }, []);

  const onRegenModeSelect = useCallback((regenMode: RegenMode) => {
    setPreferences((current) =>
      current.regenMode === regenMode ? current : { ...current, regenMode }
    );
  }, []);

  const onAssistToggle = useCallback((assist: DrivingAssistId) => {
    setPreferences((current) => ({
      ...current,
      assists: { ...current.assists, [assist]: !current.assists[assist] },
    }));
  }, []);

  const onTuningSelect = useCallback((field: VehicleTuningField, value: string) => {
    if (!isValidTuningValue(field, value)) return;
    setPreferences((current) => ({
      ...current,
      tuning: { ...current.tuning, [field]: value },
    }));
  }, []);

  const onTuningReset = useCallback(() => {
    setPreferences((current) => ({
      ...current,
      tuning: { ...DEFAULT_VEHICLE_TUNING },
    }));
  }, []);

  const course = useMemo(() => findDrivingCourseById(preferences.courseId)!, [preferences.courseId]);
  const timeOfDay = useMemo(() => findTimeOfDayById(preferences.timeOfDayId)!, [preferences.timeOfDayId]);
  const camera = useMemo(() => findDrivingCameraById(preferences.cameraMode)!, [preferences.cameraMode]);
  const challenge = useMemo(() => findDrivingChallengeById(preferences.challengeId)!, [preferences.challengeId]);

  return {
    preferences,
    course,
    timeOfDay,
    camera,
    challenge,
    onCourseSelect,
    onTimeOfDaySelect,
    onCameraSelect,
    onWeatherModeToggle,
    onChallengeSelect,
    onRegenModeSelect,
    onAssistToggle,
    onTuningSelect,
    onTuningReset,
  };
}
